import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { isLoading } from 'reference-resolvers-lite/Status';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { PIPELINE } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { ErrorBoundary } from './error/ErrorBoundary';
import RepOverviewTable from './overviewTable/RepOverviewTable';
import { getOpenPipelineValue as getOpenPipelineValueAction } from '../actions/AggregatedOverviewDataActions';
import DataWells from '../components/dataWells/DataWells';
import { FormattedPeriod } from '../components/overviewTable/FormattedPeriod';
import { CRM_SEARCH_DELAY, DEAL_OBJECT_ID } from '../constants/CrmSearchConstants';
import { getQuotasForOwnersCount, getRows, getOpenPipelineCoverage, getOpenPipelineValue, getOpenPipelineValueFetched, getTotalRow } from '../selectors/aggregatedOverviewDataSelectors';
import { getIsAllPipelines, getIsDealStageView, getSelectedForecastTypeIsDefault } from '../selectors/forecastSelectors';
import { getCompanyCurrency } from '../selectors/multiCurrencySelectors';
import { getIsOverviewDataFetching, getSelectedTeamId } from '../selectors/overviewSelectors';
import { getFiscalOffset } from '../selectors/portalSettings';
import { getUsersNotOnTeams } from '../selectors/usersSelectors';
import { getFilters, getPipelineFilter, getTimePeriodFilter } from '../selectors/filtersSelectors';
import { trackOverviewView } from '../utils/usageTracker';
import usePrevious from '../hooks/usePrevious';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HasErrorComponent = () => /*#__PURE__*/_jsx(View, {
  marginBlockStart: "200",
  as: "div"
});
const RepOverviewTableContainer = ({
  isDetailPage,
  preferredCurrency,
  showDeactivatedUserError
}) => {
  const dispatch = useDispatch();
  const companyCurrency = useSelector(getCompanyCurrency);
  const filters = useSelector(getFilters);
  const fiscalOffset = useSelector(getFiscalOffset);
  const isAllPipelines = useSelector(getIsAllPipelines);
  const isDealStageView = useSelector(getIsDealStageView);
  const isOverviewDataFetching = useSelector(getIsOverviewDataFetching);
  const openPipelineCoverage = useSelector(getOpenPipelineCoverage);
  const openPipelineValue = useSelector(getOpenPipelineValue);
  const openPipelineValueFetched = useSelector(getOpenPipelineValueFetched);
  const ownersNotAssignedTeams = useSelector(getUsersNotOnTeams);
  const quotasForOwnersCount = useSelector(getQuotasForOwnersCount);
  const selectedForecastTypeIsDefault = useSelector(getSelectedForecastTypeIsDefault);
  const selectedTeamId = useSelector(getSelectedTeamId);
  const tableData = useSelector(getRows);
  const totals = useSelector(getTotalRow);
  const selectedPipelines = useSelector(getPipelineFilter);
  const singleSelectedPipelineId = (selectedPipelines === null || selectedPipelines === void 0 ? void 0 : selectedPipelines.size) === 1 ? selectedPipelines.first() : '';
  const timePeriod = useSelector(getTimePeriodFilter);

  // We fetch the pipeline name on the container level to avoid a partial loading state
  const resolver = createStandardCachedResolver(PIPELINE, DEAL_OBJECT_ID);
  const {
    status,
    reference: singleSelectedPipeline
  } = useFetchResolver(resolver, singleSelectedPipelineId || undefined);
  const timerRef = useRef(null);
  const prevFilters = usePrevious(filters);
  const prevSelectedTeamId = usePrevious(selectedTeamId);
  const prevTotals = usePrevious(totals);
  const trackPageView = useCallback(() => {
    if (!isOverviewDataFetching && !isDetailPage) {
      trackOverviewView({
        companyCurrency,
        isDealStageView,
        ownersInTable: !!(tableData !== null && tableData !== void 0 && tableData.size),
        ownersNotAssignedTeams: ownersNotAssignedTeams === null || ownersNotAssignedTeams === void 0 ? void 0 : ownersNotAssignedTeams.size,
        preferredCurrency,
        quotasForOwners: !!quotasForOwnersCount,
        isDefaultForecast: selectedForecastTypeIsDefault
      });
    }
  }, [companyCurrency, isDealStageView, isDetailPage, isOverviewDataFetching, ownersNotAssignedTeams === null || ownersNotAssignedTeams === void 0 ? void 0 : ownersNotAssignedTeams.size, preferredCurrency, quotasForOwnersCount, selectedForecastTypeIsDefault, tableData === null || tableData === void 0 ? void 0 : tableData.size]);
  useEffect(() => {
    // If there are no previous filters, it's the first load and we should track the page view
    const filtersChanged = !prevFilters || !filters.equals(prevFilters);
    const totalsChanged = JSON.stringify(totals) !== JSON.stringify(prevTotals);
    const teamChanged = selectedTeamId !== prevSelectedTeamId;

    // Each time the filters, totals, or team change, we should start a timer to track the page view
    // If the filters change again before the timer runs out, we should clear the timer and start a new one
    // in order to avoid tracking the page view multiple times in quick succession
    if (filtersChanged || totalsChanged || teamChanged) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(trackPageView, CRM_SEARCH_DELAY);
    }

    // Cleanup function to clear the timer when the component unmounts or dependencies change
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // Need to be careful about dependencies so we don't clear the timer when we shouldn't
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedTeamId, totals]);
  useEffect(() => {
    dispatch(getOpenPipelineValueAction()).catch(() => {
      // No need to do anything here, we're already handling errors in the action
    });
  }, [dispatch, filters, selectedTeamId]);
  const teamName = totals.teamName;
  const name = !teamName && Object.values(totals).length ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "repOverviewTable.dataWells.allTeams"
  }) : teamName || /*#__PURE__*/_jsx(LoadingSpinner, {
    size: "small"
  });
  const formattedTimePeriod = /*#__PURE__*/_jsx(FormattedPeriod, {
    offset: fiscalOffset,
    timePeriod: timePeriod
  });
  const isDataFetching = !openPipelineValueFetched || isLoading(status);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!isDetailPage && /*#__PURE__*/_jsx(ErrorBoundary, {
      HasErrorComponent: HasErrorComponent,
      children: /*#__PURE__*/_jsx(DataWells, {
        formattedTimePeriod: formattedTimePeriod,
        isAllPipelines: isAllPipelines,
        isDataFetching: isDataFetching,
        name: name,
        openPipelineCoverage: openPipelineCoverage,
        openPipelineValue: openPipelineValue,
        singleSelectedPipelineId: singleSelectedPipeline === null || singleSelectedPipeline === void 0 ? void 0 : singleSelectedPipeline.id,
        timePeriod: timePeriod,
        totals: totals
      })
    }), /*#__PURE__*/_jsx(RepOverviewTable, {
      isDetailPage: isDetailPage,
      preferredCurrency: preferredCurrency,
      showDeactivatedUserError: showDeactivatedUserError,
      tableData: tableData,
      totalsRowData: totals
    })]
  });
};
export default RepOverviewTableContainer;